import type { Highlighter, ThemedToken } from 'shiki';

export type { ThemedToken };
export type HighlightedLines = ThemedToken[][];

export type HighlightTheme = 'light' | 'dark';

// Matches the themes github.com uses for PR diff syntax highlighting.
const DARK_THEME = 'github-dark-default' as const;
const LIGHT_THEME = 'github-light' as const;
const THEMES = [LIGHT_THEME, DARK_THEME] as const;

const SUPPORTED_LANGS = [
  'bash',
  'c',
  'cpp',
  'css',
  'go',
  'html',
  'java',
  'javascript',
  'json',
  'jsx',
  'kotlin',
  'markdown',
  'python',
  'ruby',
  'rust',
  'shell',
  'sql',
  'swift',
  'toml',
  'tsx',
  'typescript',
  'yaml',
] as const;

type SupportedLang = (typeof SUPPORTED_LANGS)[number];

// Shiki and all language grammars are loaded in a separate dynamic chunk on
// first use, not on app startup.
let highlighterPromise: Promise<Highlighter> | null = null;

/**
 * Storybook play-function tests set `globalThis.__REFLEX_DISABLE_SYNTAX_HIGHLIGHT__`
 * so {@link highlightCode} renders code as a single plain-text node instead of
 * Shiki's per-token `<span>`s.
 *
 * Highlighting runs asynchronously, so the text is split across nested spans
 * partway through a render. Exact `getByText(command)` assertions then flake
 * depending on whether the highlight pass has landed yet -- which it does much
 * faster for later stories once Shiki is cached across files in a shared worker
 * (the local `isolate: false` run). Visual/Chromatic snapshots are captured
 * from the built Storybook, not this run, so they keep real highlighting.
 */
function syntaxHighlightingDisabled(): boolean {
  return (
    typeof globalThis !== 'undefined' &&
    (globalThis as { __REFLEX_DISABLE_SYNTAX_HIGHLIGHT__?: boolean })
      .__REFLEX_DISABLE_SYNTAX_HIGHLIGHT__ === true
  );
}

function getHighlighter(): Promise<Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = import('shiki').then(({ createHighlighter }) =>
      createHighlighter({
        themes: [...THEMES],
        langs: [...SUPPORTED_LANGS],
      }),
    );
  }
  return highlighterPromise;
}

const EXT_TO_LANG: Record<string, SupportedLang> = {
  js: 'javascript',
  cjs: 'javascript',
  mjs: 'javascript',
  jsx: 'jsx',
  ts: 'typescript',
  cts: 'typescript',
  mts: 'typescript',
  tsx: 'tsx',
  py: 'python',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  json: 'json',
  yaml: 'yaml',
  yml: 'yaml',
  toml: 'toml',
  css: 'css',
  html: 'html',
  htm: 'html',
  md: 'markdown',
  rs: 'rust',
  go: 'go',
  java: 'java',
  c: 'c',
  cpp: 'cpp',
  cc: 'cpp',
  h: 'c',
  hpp: 'cpp',
  rb: 'ruby',
  swift: 'swift',
  kt: 'kotlin',
  sql: 'sql',
};

const SHELL_DOTFILES = new Set([
  '.profile',
  '.bashrc',
  '.bash_profile',
  '.bash_logout',
  '.bash_history',
  '.bash_interactive',
  '.zshrc',
  '.zprofile',
  '.zshenv',
  '.zlogout',
]);

function detectLang(filename: string): SupportedLang | null {
  const base = filename.includes('/') ? filename.slice(filename.lastIndexOf('/') + 1) : filename;
  if (SHELL_DOTFILES.has(base.toLowerCase())) return 'bash';
  if (base.toLowerCase() === 'dockerfile') return 'shell';
  if (base.toLowerCase() === 'makefile') return 'shell';

  const ext = base.split('.').pop()?.toLowerCase() ?? '';
  return EXT_TO_LANG[ext] ?? null;
}

/**
 * Highlights code and returns per-line token arrays.
 * Returns null when the language is unknown or an error occurs — callers
 * should fall back to rendering plain text in that case.
 *
 * @param theme Which Shiki theme to use. Dark token colors are unreadable
 *   on a light background, so callers should pass the active app theme.
 * @param languageHint Optional pre-detected language id (e.g. from a file stat).
 *   Falls back to extension-based detection from `filename` when omitted.
 */
export async function highlightCode(
  code: string,
  filename: string,
  theme: HighlightTheme,
  languageHint?: string | null,
): Promise<HighlightedLines | null> {
  if (syntaxHighlightingDisabled()) return null;

  const hint = languageHint?.toLowerCase() as SupportedLang | undefined;
  const lang =
    (hint && SUPPORTED_LANGS.includes(hint as SupportedLang) ? hint : null) ?? detectLang(filename);
  if (!lang || !code) return null;

  try {
    const hl = await getHighlighter();
    return hl.codeToTokensBase(code, {
      lang,
      theme: theme === 'light' ? LIGHT_THEME : DARK_THEME,
    });
  } catch {
    return null;
  }
}
